import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import DiploksConsentModal from "./components/DiploksConsentModal";
import DiploksLowBalanceDialog from "./components/DiploksLowBalanceDialog";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import WhatsAppFloating from "./components/WhatsAppFloating";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PublicEvent from "./pages/PublicEvent";
import VerifyCertificate from "./pages/VerifyCertificate";
import VerifyScanPage from "./pages/VerifyScanPage";
import DownloadPage from "./pages/DownloadPage";
import InviteRedirectPage from "./pages/InviteRedirectPage";
import MobileAuthBridge from "./pages/MobileAuthBridge";
import OrganisationOnboardingPage from "./pages/OrganisationOnboardingPage";
import ProfilePage from "./pages/ProfilePage";
import AdminInvitesPage from "./pages/admin/AdminInvitesPage";
import SolutionsPage from "./pages/SolutionsPage";
import PricingPage from "./pages/PricingPage";
import PricingPageV2 from "./pages/PricingPageV2";
import PolicyViewerPage from "./pages/PolicyViewerPage";
import ContactPageV2 from "./pages/ContactPageV2";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentResultPage from "./pages/PaymentResultPage";
import BillingCenterPage from "./pages/BillingCenterPage";
import InvoiceViewerPage from "./pages/InvoiceViewerPage";
import DiploksWalletPage from "./pages/DiploksWalletPage";
import DiploksTopUpPage from "./pages/DiploksTopUpPage";
import PersonalDiploksWalletPage from "./pages/PersonalDiploksWalletPage";
import PersonalDiploksTopUpPage from "./pages/PersonalDiploksTopUpPage";
import OrganiserGrantsPage from "./pages/OrganiserGrantsPage";
import ParentChildrenPage from "./pages/ParentChildrenPage";
import AutoTopUpSettingsPage from "./pages/AutoTopUpSettingsPage";
import EventRBACPhaseBPage from "./pages/EventRBACPhaseBPage";
import RBACPhaseCPage from "./pages/RBACPhaseCPage";
import FeatureCostRulesAdminPage from "./pages/admin/FeatureCostRulesAdminPage";
import AdminReconciliationDashboard from "./pages/admin/AdminReconciliationDashboard";
import AdminAdjustmentsPage from "./pages/admin/AdminAdjustmentsPage";
import AdminVouchersPromosPage from "./pages/admin/AdminVouchersPromosPage";
import RevenueDashboardPage from "./pages/admin/RevenueDashboardPage";
import GatewayReviewPage from "./pages/admin/GatewayReviewPage";
import WatchdogPage from "./pages/admin/WatchdogPage";
import CommitteeRoomPage from "./pages/delegate/CommitteeRoomPage";
import ChairHybridDashboard from "./pages/chair/ChairHybridDashboard";
import ScenarioQuizPage from "./pages/delegate/ScenarioQuizPage";
import ResolutionSandboxPage from "./pages/delegate/ResolutionSandboxPage";
import LearningPathPage from "./pages/delegate/LearningPathPage";
import CookieConsentBanner from "./components/CookieConsentBanner";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import DemoPage from "./pages/DemoPage";
import DemoLoginsPage from "./pages/DemoLoginsPage";

import SuperAdminDashboard from "./pages/admin/SuperAdminDashboard";
import OrganizationsPage from "./pages/admin/OrganizationsPage";
import AdminEventsPage from "./pages/admin/AdminEventsPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import LMSAnalyticsPage from "./pages/admin/LMSAnalyticsPage";
import PrivacyCenterPage from "./pages/PrivacyCenterPage";
import BrandingSettingsPage from "./pages/admin/BrandingSettingsPage";
import CrmPage from "./pages/admin/CrmPage";
import EventDetailPage from "./pages/admin/EventDetailPage";
import EventRoleSetupPage from "./pages/admin/EventRoleSetupPage";
import DiploksAdminPage from "./pages/admin/DiploksAdminPage";

import DelegateDashboard from "./pages/delegate/DelegateDashboard";
import DelegateEventsPage from "./pages/delegate/DelegateEventsPage";
import CourseView from "./pages/delegate/CourseView";
import LMSLearnerDashboardPage from "./pages/lms/LMSLearnerDashboardPage";
import LMSMunPrepHubPage from "./pages/lms/LMSMunPrepHubPage";
import LMSTrainerDashboardPage from "./pages/lms/LMSTrainerDashboardPage";
import LMSChairDashboardPage from "./pages/lms/LMSChairDashboardPage";
import LMSFacultyDashboardPage from "./pages/lms/LMSFacultyDashboardPage";
import LMSParentDashboardPage from "./pages/lms/LMSParentDashboardPage";
import LMSAdminCourseBuilderPage from "./pages/lms/LMSAdminCourseBuilderPage";
// Sprint EVENT-ACCESS — Diplomexa AccessPass
import VisitorRegisterPage from "./pages/VisitorRegisterPage";
import MyAccessPassPage from "./pages/access/MyAccessPassPage";
import AccessScannerPage from "./pages/access/AccessScannerPage";
import EventAccessControlPage from "./pages/admin/EventAccessControlPage";
// Sprint LMS-Live — Diplomexa Live Classroom
import LiveSessionsListPage from "./pages/lms-live/LiveSessionsListPage";
import LiveSessionDetailPage from "./pages/lms-live/LiveSessionDetailPage";
import LiveSessionSchedulerPage from "./pages/lms-live/LiveSessionSchedulerPage";
import ParentLiveSummaryPage from "./pages/lms-live/ParentLiveSummaryPage";
import SuperAdminLiveProviderSettingsPage from "./pages/lms-live/SuperAdminLiveProviderSettingsPage";
import LiveClassAnalyticsPage from "./pages/lms-live/LiveClassAnalyticsPage";
import AdminGoodwillCreditAuditPage from "./pages/admin/AdminGoodwillCreditAuditPage";
// Sprint LMS-Gamify — Podium Points, Badges, Streaks
import PodiumJourneyPage from "./pages/gamification/PodiumJourneyPage";
import AdminPodiumRulesPage from "./pages/gamification/AdminPodiumRulesPage";
// Sprint LMS-Growth — Trainer Grading Queue
import TrainerGradingQueuePage from "./pages/trainer/TrainerGradingQueuePage";
import PapersPage from "./pages/delegate/PapersPage";
import CertificatesPage from "./pages/delegate/CertificatesPage";

import ChairDashboard from "./pages/chair/ChairDashboard";
import LiveSessionPage from "./pages/chair/LiveSessionPage";
import ChairAwardsPage from "./pages/chair/ChairAwardsPage";

import SchoolAdminDashboard from "./pages/school/SchoolAdminDashboard";
import SchoolDelegatesPage from "./pages/school/SchoolDelegatesPage";
import SchoolInvoicesPage from "./pages/school/SchoolInvoicesPage";
import FacultyAdvisorDashboard from "./pages/faculty/FacultyAdvisorDashboard";
import FacultySchedulePage from "./pages/faculty/FacultySchedulePage";
import EventManagerDashboard from "./pages/event_manager/EventManagerDashboard";
import EventCreationWizard from "./pages/admin/EventCreationWizard";
import VolunteerDashboard from "./pages/volunteer/VolunteerDashboard";
import AttendancePage from "./pages/volunteer/AttendancePage";
import ParentDashboard from "./pages/parent/ParentDashboard";
import AuditLogsPage from "./pages/admin/AuditLogsPage";
import PassportPage from "./pages/delegate/PassportPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import DelegatePrepAssistantPage from "./pages/delegate/DelegatePrepAssistantPage";
import ChairAssistantPage from "./pages/chair/ChairAssistantPage";
import PositionPaperBuilderPage from "./pages/delegate/PositionPaperBuilderPage";
import ResolutionBuilderPage from "./pages/delegate/ResolutionBuilderPage";
import MyPapersPage from "./pages/delegate/MyPapersPage";
import MyResolutionsPage from "./pages/delegate/MyResolutionsPage";

// Phase D
import SponsorDashboard from "./pages/sponsor/SponsorDashboard";
import JudgeDashboard from "./pages/judge/JudgeDashboard";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import HelpdeskPage from "./pages/HelpdeskPage";
import AutomationCenterPage from "./pages/admin/AutomationCenterPage";
import PollsCrisisPage from "./pages/PollsCrisisPage";
import EventWebsiteBuilderPage from "./pages/admin/EventWebsiteBuilderPage";
import EmailTemplatePreviewPage from "./pages/admin/EmailTemplatePreviewPage";
import DiplomexaIntelligencePreviewPage from "./pages/admin/DiplomexaIntelligencePreviewPage";
import EmailEventsPage from "./pages/admin/EmailEventsPage";
import NotFoundPage from "./pages/NotFoundPage";

function RoleRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <SuperAdminDashboard />;
  if (user.role === "org_admin") return <SuperAdminDashboard />;
  if (user.role === "event_manager") return <EventManagerDashboard />;
  if (user.role === "chair") return <ChairDashboard />;
  if (user.role === "school_admin") return <SchoolAdminDashboard />;
  if (user.role === "faculty_advisor") return <FacultyAdvisorDashboard />;
  if (user.role === "volunteer") return <VolunteerDashboard />;
  if (user.role === "parent") return <ParentDashboard />;
  if (user.role === "sponsor") return <SponsorDashboard />;
  if (user.role === "judge") return <JudgeDashboard />;
  if (user.role === "partner_institution") return <PartnerDashboard />;
  return <DelegateDashboard />;
}

function EventsRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "delegate" || user.role === "parent") return <DelegateEventsPage />;
  return <AdminEventsPage />;
}

/**
 * Smart 404 — if the user is signed in, render a friendly in-portal
 * NotFoundPage with role-aware suggestions (keeps them inside the
 * authed shell, no silent bounce).  If they're anonymous, send them
 * to the public landing page.
 */
function NotFoundRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;            // Auth check in progress
  if (user) return <NotFoundPage />;
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/pricing" element={<PricingPageV2 />} />
            <Route path="/pricing-legacy" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPageV2 />} />
            <Route path="/terms" element={<PolicyViewerPage policyKey="terms" />} />
            <Route path="/privacy" element={<PolicyViewerPage policyKey="privacy" />} />
            <Route path="/refund-policy" element={<PolicyViewerPage policyKey="refund_policy" />} />
            <Route path="/cancellation-policy" element={<PolicyViewerPage policyKey="cancellation_policy" />} />
            <Route path="/cookie-policy" element={<PolicyViewerPage policyKey="cookie_policy" />} />
            <Route path="/cookies" element={<PolicyViewerPage policyKey="cookie_policy" />} />
            <Route path="/billing-notice" element={<PolicyViewerPage policyKey="billing_notice" />} />
            <Route path="/shipping-policy" element={<PolicyViewerPage policyKey="shipping_policy" />} />
            <Route path="/delivery-policy" element={<PolicyViewerPage policyKey="shipping_policy" />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/demo-logins" element={<DemoLoginsPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/events/:slug" element={<PublicEvent />} />
            <Route path="/events/:slug/visitor-register" element={<VisitorRegisterPage />} />
            <Route path="/verify/:code" element={<VerifyCertificate />} />
            <Route path="/verify" element={<VerifyCertificate />} />
            <Route path="/scan" element={<VerifyScanPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/i/:slug" element={<InviteRedirectPage />} />
            <Route path="/auth/mobile-bridge" element={<MobileAuthBridge />} />

            {/* Org onboarding wizard — gated to authed users only, but
                MUST be reachable by org_admin/event_manager who don't
                yet have an organization_id linked (the very state that
                makes the rest of the dashboard refuse to load). */}
            <Route
              path="/onboarding/organisation"
              element={
                <ProtectedRoute allowMissingOrg>
                  <OrganisationOnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Profile — every authed user, regardless of org link. */}
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute allowMissingOrg>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Protected */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/organizations"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <OrganizationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/events"
              element={
                <ProtectedRoute>
                  <EventsRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/events/new"
              element={
                <ProtectedRoute roles={["org_admin", "event_manager", "super_admin"]}>
                  <EventCreationWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/events/:eventId"
              element={
                <ProtectedRoute roles={["org_admin", "chair"]}>
                  <EventDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/analytics"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <AdminAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms-analytics"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager"]}>
                  <LMSAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/privacy"
              element={
                <ProtectedRoute roles={["delegate", "chair", "judge", "parent", "school_admin", "faculty_advisor", "volunteer", "sponsor", "partner_institution", "org_admin", "event_manager", "super_admin"]}>
                  <PrivacyCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing"
              element={
                <ProtectedRoute>
                  <BillingCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/wallet"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager", "school_admin"]}>
                  <DiploksWalletPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/topup"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager", "school_admin"]}>
                  <DiploksTopUpPage />
                </ProtectedRoute>
              }
            />
            {/* Universal personal wallet — every eligible role.
                Sponsor / partner_institution are blocked by ProtectedRoute role
                list AND server-side platform-policy eligibility check. */}
            <Route
              path="/dashboard/diploks/personal/wallet"
              element={
                <ProtectedRoute
                  roles={[
                    "super_admin", "org_admin", "event_manager", "school_admin",
                    "chair", "delegate", "faculty_advisor", "parent",
                    "volunteer", "judge",
                  ]}
                >
                  <PersonalDiploksWalletPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/personal/topup"
              element={
                <ProtectedRoute
                  roles={[
                    "super_admin", "org_admin", "event_manager", "school_admin",
                    "chair", "delegate", "faculty_advisor", "parent",
                    "volunteer", "judge",
                  ]}
                >
                  <PersonalDiploksTopUpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/organiser/grants"
              element={
                <ProtectedRoute
                  roles={["super_admin", "org_admin", "event_manager", "school_admin"]}
                >
                  <OrganiserGrantsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/parent/children"
              element={
                <ProtectedRoute roles={["parent", "super_admin"]}>
                  <ParentChildrenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/diploks/auto-topup"
              element={
                <ProtectedRoute
                  roles={[
                    "super_admin", "org_admin", "event_manager", "school_admin",
                    "chair", "delegate", "faculty_advisor", "parent",
                    "volunteer", "judge",
                  ]}
                >
                  <AutoTopUpSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/events/:eventId/rbac"
              element={
                <ProtectedRoute
                  roles={["super_admin", "org_admin", "event_manager"]}
                >
                  <EventRBACPhaseBPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/rbac/advanced"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <RBACPhaseCPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/events/:eventId/roles"
              element={
                <ProtectedRoute>
                  <EventRoleSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/diploks"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <DiploksAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/diploks/feature-costs"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <FeatureCostRulesAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/diploks/reconciliation"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <AdminReconciliationDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/diploks/adjustments"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <AdminAdjustmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/diploks/vouchers-promos"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <AdminVouchersPromosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing/success"
              element={
                <ProtectedRoute>
                  <PaymentResultPage defaultStatus="paid" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing/cancel"
              element={
                <ProtectedRoute>
                  <PaymentResultPage defaultStatus="failed" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing/failed"
              element={
                <ProtectedRoute>
                  <PaymentResultPage defaultStatus="failed" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing/invoice/:invoiceId"
              element={
                <ProtectedRoute>
                  <InvoiceViewerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/revenue"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <RevenueDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/gateway-review"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <GatewayReviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/watchdog"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <WatchdogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/email-templates"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <EmailTemplatePreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/ai-ux-preview"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <DiplomexaIntelligencePreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/email-events"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <EmailEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId/committee-room"
              element={
                <ProtectedRoute>
                  <CommitteeRoomPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId/chair-floor"
              element={
                <ProtectedRoute roles={["chair", "event_manager", "secretariat", "org_admin", "super_admin"]}>
                  <ChairHybridDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/scenarios"
              element={
                <ProtectedRoute>
                  <ScenarioQuizPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/sandbox"
              element={
                <ProtectedRoute>
                  <ResolutionSandboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/road-to-podium"
              element={
                <ProtectedRoute>
                  <LearningPathPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <BrandingSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/crm"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <CrmPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/courses"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <CourseView />
                </ProtectedRoute>
              }
            />
            {/* Sprint LMS-X — Premium Global Delegate Preparation Academy */}
            <Route
              path="/dashboard/lms"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                  ]}
                >
                  <LMSLearnerDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/mun-prep"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                  ]}
                >
                  <LMSMunPrepHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/trainer"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager"]}>
                  <LMSTrainerDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/chair"
              element={
                <ProtectedRoute roles={["chair", "super_admin", "org_admin"]}>
                  <LMSChairDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/faculty"
              element={
                <ProtectedRoute roles={["faculty_advisor", "school_admin", "super_admin", "org_admin"]}>
                  <LMSFacultyDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/parent"
              element={
                <ProtectedRoute roles={["parent", "super_admin"]}>
                  <LMSParentDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/admin/course-builder"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <LMSAdminCourseBuilderPage />
                </ProtectedRoute>
              }
            />
            {/* Sprint LMS-Live — Diplomexa Live Classroom */}
            <Route
              path="/dashboard/lms/live-sessions"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                  ]}
                >
                  <LiveSessionsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/live-sessions/scheduler"
              element={
                <ProtectedRoute
                  roles={["chair", "faculty_advisor",
                            "event_manager", "org_admin", "super_admin"]}
                >
                  <LiveSessionSchedulerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/live-sessions/:sessionId"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                  ]}
                >
                  <LiveSessionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/parent/live"
              element={
                <ProtectedRoute roles={["parent", "super_admin"]}>
                  <ParentLiveSummaryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/admin/live-provider-settings"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <SuperAdminLiveProviderSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/lms/live-analytics"
              element={
                <ProtectedRoute
                  roles={[
                    "chair", "faculty_advisor", "event_manager",
                    "org_admin", "super_admin",
                  ]}
                >
                  <LiveClassAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/goodwill-audit"
              element={
                <ProtectedRoute
                  roles={["super_admin", "org_admin", "event_manager"]}
                >
                  <AdminGoodwillCreditAuditPage />
                </ProtectedRoute>
              }
            />

            {/* Sprint LMS-Growth — Trainer Grading Queue */}
            <Route
              path="/dashboard/trainer/grading-queue"
              element={
                <ProtectedRoute
                  roles={[
                    "chair", "faculty_advisor", "org_admin",
                    "event_manager", "school_admin", "super_admin",
                  ]}
                >
                  <TrainerGradingQueuePage />
                </ProtectedRoute>
              }
            />

            {/* Sprint LMS-Gamify */}
            <Route
              path="/dashboard/podium-journey"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                  ]}
                >
                  <PodiumJourneyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/podium-rules"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager"]}>
                  <AdminPodiumRulesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/papers/legacy"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <PapersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/certificates"
              element={
                <ProtectedRoute roles={["delegate", "org_admin", "super_admin"]}>
                  <CertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/live"
              element={
                <ProtectedRoute roles={["chair", "org_admin"]}>
                  <LiveSessionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/live/:sessionId"
              element={
                <ProtectedRoute roles={["chair", "org_admin"]}>
                  <LiveSessionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/awards"
              element={
                <ProtectedRoute roles={["chair", "org_admin"]}>
                  <ChairAwardsPage />
                </ProtectedRoute>
              }
            />

            {/* School admin + faculty routes */}
            <Route
              path="/dashboard/school/delegates"
              element={
                <ProtectedRoute roles={["school_admin", "faculty_advisor"]}>
                  <SchoolDelegatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/school/invoices"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <SchoolInvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/faculty/schedule"
              element={
                <ProtectedRoute roles={["faculty_advisor", "delegate"]}>
                  <FacultySchedulePage />
                </ProtectedRoute>
              }
            />

            {/* Volunteer */}
            <Route
              path="/dashboard/volunteer/attendance"
              element={
                <ProtectedRoute roles={["volunteer", "event_manager", "org_admin", "chair"]}>
                  <AttendancePage />
                </ProtectedRoute>
              }
            />

            {/* Audit (super admin) */}
            <Route
              path="/dashboard/audit"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <AuditLogsPage />
                </ProtectedRoute>
              }
            />

            {/* Passport + Leaderboard (Phase B) */}
            <Route
              path="/dashboard/passport"
              element={
                <ProtectedRoute>
                  <PassportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/passport/:delegateId"
              element={
                <ProtectedRoute>
                  <PassportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />

            {/* Phase C — AI Assistants + Builders */}
            <Route
              path="/dashboard/ai-prep"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <DelegatePrepAssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/chair-assist"
              element={
                <ProtectedRoute roles={["chair", "org_admin", "super_admin", "event_manager"]}>
                  <ChairAssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/papers"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <MyPapersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/papers/new"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <PositionPaperBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/papers/:paperId"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <PositionPaperBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/resolutions"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <MyResolutionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/resolutions/new"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <ResolutionBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/resolutions/:resolutionId"
              element={
                <ProtectedRoute roles={["delegate"]}>
                  <ResolutionBuilderPage />
                </ProtectedRoute>
              }
            />

            {/* Phase D — Sponsor / Judge / Partner / Helpdesk / Automation / Polls / Crisis */}
            <Route
              path="/dashboard/sponsor"
              element={
                <ProtectedRoute roles={["sponsor", "super_admin", "org_admin"]}>
                  <SponsorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/judge"
              element={
                <ProtectedRoute roles={["judge", "super_admin", "org_admin"]}>
                  <JudgeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/partner"
              element={
                <ProtectedRoute roles={["partner_institution", "super_admin", "org_admin"]}>
                  <PartnerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/helpdesk"
              element={
                <ProtectedRoute>
                  <HelpdeskPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/automation"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager"]}>
                  <AutomationCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/polls"
              element={
                <ProtectedRoute>
                  <PollsCrisisPage />
                </ProtectedRoute>
              }
            />

            {/* Phase F — Event Website Builder */}
            <Route
              path="/dashboard/events/:eventId/builder"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin", "event_manager"]}>
                  <EventWebsiteBuilderPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/invites"
              element={
                <ProtectedRoute roles={["super_admin", "org_admin"]}>
                  <AdminInvitesPage />
                </ProtectedRoute>
              }
            />

            {/* Sprint EVENT-ACCESS — Diplomexa AccessPass */}
            <Route
              path="/dashboard/access-passes"
              element={
                <ProtectedRoute
                  roles={[
                    "delegate", "chair", "faculty_advisor", "parent",
                    "volunteer", "judge", "school_admin",
                    "event_manager", "org_admin", "super_admin",
                    "sponsor", "partner_institution",
                  ]}
                >
                  <MyAccessPassPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/access/scanner"
              element={
                <ProtectedRoute
                  roles={["super_admin", "org_admin",
                           "event_manager", "volunteer"]}
                >
                  <AccessScannerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/access-control"
              element={
                <ProtectedRoute
                  roles={["super_admin", "org_admin", "event_manager"]}
                >
                  <EventAccessControlPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
          <CookieConsentBanner />
          <DiploksConsentModal />
          <DiploksLowBalanceDialog />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <WhatsAppFloating />
      </AuthProvider>
    </div>
  );
}

export default App;
